import { useEffect, useCallback, useRef } from 'react'
import { useLatest } from 'react-use'
import { isFunction, find } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { ThreeEvent } from '@react-three/fiber'
import { useControlStore } from '@editorStores'
import { useClickActionEnabled, useIfcImportSelectionActive } from '@editorHooks'
import { isPointerModifierPressed } from 'src/shortcuts/pointerModifiers'

const emptyArray: unknown[] = []

/**
 * TODO: make configuration via config object (difficult because of rerendering)
 * @param callback
 * @param dependencies
 * @param enable
 * @param isExclusive
 * @param enableOnMultiSelect
 * @param enableOnIfcMode
 * @returns
 */
const useModelClickListeners = (
  callback?: ModelClickListener,
  dependencies: unknown[] = emptyArray,
  enable = true,
  isExclusive = false,
  /**
   * Enable this callback while the multi-select modifier is held
   */
  enableOnMultiSelect = false,
  /**
   * Enable this callback even when ifc mode is active (working on ifc model)
   */
  enableOnIfcMode = false,
): ModelClickListener => {
  const id = useRef<string>(uuid())
  const clickListeners = useControlStore(state => state.clickListeners)
  const setClickListener = useControlStore(state => state.setClickListener)
  const removeClickListener = useControlStore(state => state.removeClickListener)
  const ifcDrawer = useIfcImportSelectionActive()

  const drawControllerEnabled = useClickActionEnabled()

  const latestEnabled = useLatest(drawControllerEnabled)
  const latestIfcDrawer = useLatest(ifcDrawer)
  const latestClickListeners = useLatest(clickListeners)

  const executeListeners = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (!latestEnabled.current) return

      const listeners = Array.from(latestClickListeners.current.values())
      const exclusiveListener = find(listeners, ['isExclusive', true])
      if (exclusiveListener) {
        exclusiveListener.listener(event)
        return
      }

      const isMultiSelectPressed = isPointerModifierPressed('multiSelect')

      listeners.forEach(({ listener, enableOnMultiSelect, enableOnIfcMode }) => {
        if (isMultiSelectPressed && !enableOnMultiSelect) return

        if (latestIfcDrawer.current && !enableOnIfcMode) return

        listener(event)
      })
    },
    [latestEnabled, latestIfcDrawer, latestClickListeners],
  )

  useEffect(() => {
    if (!enable || !isFunction(callback)) return
    setClickListener(id.current, callback, isExclusive, enableOnMultiSelect, enableOnIfcMode)

    return () => removeClickListener(id.current)
  }, [enable, enableOnMultiSelect, ...dependencies])

  return executeListeners
}

export default useModelClickListeners
